import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import Home from './Home';
import Payment from './Payment';
import Success from './Success';
import Cancel from './Cancel';
import DarkToggle from './DarkToggle';
import Login from './Login';
import Register from './Register';
import Timetable from './Timetable';
import Topics from './Topics';
import NotesBrowser from './NotesBrowser';
import AdminUpload from './AdminUpload';
import AdminPapers from './AdminPapers';
import AdminNotesBuilder from './AdminNotesBuilder';
import { AdminNotesJobs, AdminNotesJobDetail, AdminNotesOutputView } from './AdminNotesJobs';
import AdminCalendarModeration from './AdminCalendarModeration';
import AdminUsers from './AdminUsers';
import Viewer from './viewer/Viewer';
import GenerateQuestions from './GenerateQuestions';
import AIMarker from './AIMarker';
import Calculator from './Calculator';
import Dashboard from './Dashboard';
import CalendarList from './calendars/CalendarList';
import CalendarEdit from './calendars/CalendarEdit';
import CalendarView from './calendars/CalendarView';
import { AuthProvider, useAuth } from './AuthContext';
import './App.css';

function NavBar() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Close menu on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Lock body scroll while drawer open on mobile
  useEffect(() => {
    if (open) document.body.classList.add('nav-open');
    else document.body.classList.remove('nav-open');
    return () => document.body.classList.remove('nav-open');
  }, [open]);

  return (
    <nav className="top-nav">
      <Link to="/" className="logo-link" onClick={() => setOpen(false)}>Student Resource Hub</Link>
      <button
        type="button"
        className={`nav-toggle${open ? ' open' : ''}`}
        aria-label="Toggle navigation"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span /><span /><span />
      </button>
      <div className={`nav-actions${open ? ' open' : ''}`}>
        <Link to="/" className="nav-link">Home</Link>
        <Link to="/timetable" className="nav-link">Revision timetable</Link>
        <Link to="/topics" className="nav-link">Topic tracker</Link>
        <Link to="/notes" className="nav-link">Notes</Link>
        <Link to="/calendars" className="nav-link">Calendars</Link>
        <Link to="/calculator" className="nav-link">Calculator</Link>
        {user?.isAdmin && <Link to="/generate_questions" className="nav-link">Question Generator</Link>}
        {user?.isAdmin && <Link to="/ai-marker" className="nav-link">AI Marker</Link>}
        {user?.isAdmin && <Link to="/admin" className="nav-link">Admin</Link>}
        {user?.isAdmin && <Link to="/admin/papers" className="nav-link">Papers</Link>}
        {user?.isAdmin && <Link to="/admin/notes" className="nav-link">Notes builder</Link>}
        {user?.isAdmin && <Link to="/admin/calendars" className="nav-link">Cal mod</Link>}
        {user?.isAdmin && <Link to="/admin/users" className="nav-link">Users</Link>}
        {user ? (
          <Link to="/dashboard" className="nav-link nav-user" title="My dashboard">{user.email}</Link>
        ) : (
          <Link to="/login" className="nav-link">Log in</Link>
        )}
        <DarkToggle />
      </div>
      {open && <div className="nav-backdrop" onClick={() => setOpen(false)} />}
    </nav>
  );
}

function Forbidden({ user }) {
  return (
    <div className="tt-page" style={{ maxWidth: 640, textAlign: 'center' }}>
      <h1>🔒 Admins only</h1>
      <p style={{ color: 'var(--muted)' }}>
        {user
          ? 'Your account doesn\u2019t have access to this page.'
          : <>You need to <Link to="/login">log in</Link> with an admin account to view this page.</>}
      </p>
      <p><Link to="/" className="primary-btn" style={{ display: 'inline-block', marginTop: 12 }}>← Back to home</Link></p>
    </div>
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Wrap admin-only pages: while auth is loading, render nothing; otherwise
  // show the page for admins or a friendly Forbidden screen for everyone else.
  const adminOnly = (node) => {
    if (loading) return null;
    if (user?.isAdmin) return node;
    return <Forbidden user={user} />;
  };

  // Wrap pages that require any logged-in user. Anonymous viewers are sent to
  // /login with a `next` param so they return after signing in.
  const loginRequired = (node) => {
    if (loading) return null;
    if (user) return node;
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  };

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/payment" element={<Payment />} />
      <Route path="/success" element={<Success />} />
      <Route path="/cancel" element={<Cancel />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/timetable" element={<Timetable />} />
      <Route path="/topics" element={<Topics />} />
      <Route path="/notes" element={<NotesBrowser />} />
      <Route path="/admin" element={adminOnly(<AdminUpload />)} />
      <Route path="/admin/papers" element={adminOnly(<AdminPapers />)} />
      <Route path="/admin/notes" element={adminOnly(<AdminNotesBuilder />)} />
      <Route path="/admin/notes/jobs" element={adminOnly(<AdminNotesJobs />)} />
      <Route path="/admin/notes/jobs/:id" element={adminOnly(<AdminNotesJobDetail />)} />
      <Route path="/admin/notes/jobs/:id/outputs/:outputId" element={adminOnly(<AdminNotesOutputView />)} />
      <Route path="/admin/calendars" element={adminOnly(<AdminCalendarModeration />)} />
      <Route path="/admin/users" element={adminOnly(<AdminUsers />)} />
      <Route path="/viewer/:paperId" element={<Viewer />} />
      <Route path="/generate_questions" element={adminOnly(<GenerateQuestions />)} />
      <Route path="/ai-marker" element={adminOnly(<AIMarker />)} />
      <Route path="/calculator" element={<Calculator />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/calendars" element={<CalendarList />} />
      <Route path="/calendars/new" element={loginRequired(<CalendarEdit />)} />
      <Route path="/calendars/:slug" element={<CalendarView />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <NavBar />
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
}

export default App;